import { DateTime } from 'luxon';
import { TripLegSearchResponseItem } from '../data/models/trip-leg-search.model';
import { TripLegStatusBC } from '../data/models/trip-leg-status.model';
import { tripStatusNoLoadList } from '../data/models/trip-leg-status.model';

/**
 * Checks if a trip leg has all required timestamps based on its status.
 *
 * Required timestamps vary by status:
 * - Completed trips: onSite, onBoard, and completed
 * - Canceled late trips: canceledLate
 * - No load trips: onSite and noLoad
 *
 * @param tripLeg - The trip leg to check
 * @returns true if all required timestamps are present, false otherwise
 */
export function checkTimestampsFilled(
  tripLeg: Pick<
    TripLegSearchResponseItem,
    'tripLegStatusLogs' | 'tripLegStatusBC'
  >,
): boolean {
  const statusLogs = tripLeg.tripLegStatusLogs;
  if (!statusLogs) return false;

  const currentStatus = tripLeg.tripLegStatusBC as TripLegStatusBC;

  const hasOnsite = statusLogs.find(
    ({ tripLegStatusBC }) => tripLegStatusBC === TripLegStatusBC.Onsite,
  );
  const hasOnboard = statusLogs.find(
    ({ tripLegStatusBC }) => tripLegStatusBC === TripLegStatusBC.Onboard,
  );
  const hasCompleted = statusLogs.find(
    ({ tripLegStatusBC }) => tripLegStatusBC === TripLegStatusBC.Completed,
  );
  const hasCanceledLate = statusLogs.find(
    ({ tripLegStatusBC }) => tripLegStatusBC === TripLegStatusBC.CanceledLate,
  );
  const hasNoLoad = statusLogs.find(({ tripLegStatusBC }) =>
    tripStatusNoLoadList.includes(tripLegStatusBC as TripLegStatusBC),
  );

  if (tripStatusNoLoadList.includes(currentStatus)) {
    return Boolean(hasOnsite && hasNoLoad);
  }
  if (currentStatus === TripLegStatusBC.CanceledLate) {
    return Boolean(hasCanceledLate);
  }
  return Boolean(hasOnsite && hasOnboard && hasCompleted);
}

/**
 * Compares two timestamps to minute precision.
 *
 * @param optimisticTime - The optimistic timestamp (ISO string)
 * @param backendTime - The backend timestamp (ISO string)
 * @returns true if timestamps match to the minute, or if either is missing
 */
export function timestampsMatchToMinute(
  optimisticTime: string | undefined,
  backendTime: string | undefined,
): boolean {
  if (!optimisticTime || !backendTime) {
    return true;
  }
  const backendDateTime = DateTime.fromISO(backendTime);
  const optimisticDateTime = DateTime.fromISO(optimisticTime);
  return (
    backendDateTime.startOf('minute').toISO() ===
    optimisticDateTime.startOf('minute').toISO()
  );
}

/**
 * Validates the no-load timestamp business rules.
 *
 * A no-load timestamp must be:
 * - later than the On Site timestamp
 * - at least X minutes later than Pickup Due time
 *
 * @param noLoadDateTime - The proposed No Load timestamp
 * @param onSiteDateTime - The On Site timestamp, when available
 * @param pickupDueDateTime - The Pickup Due timestamp, when available
 * @param noLoadMinWaitTime - The minimum wait time in minutes, defaults to 10
 * @returns true when the No Load timestamp violates either rule
 */
export function isInvalidNoLoadTime(
  noLoadDateTime: DateTime,
  onSiteDateTime?: DateTime | null,
  pickupDueDateTime?: DateTime | null,
  noLoadMinWaitTime: number = 10,
): boolean {
  if (!noLoadDateTime.isValid) {
    return false;
  }

  const normalizedNoLoadDateTime = noLoadDateTime.startOf('minute');

  if (
    onSiteDateTime?.isValid &&
    normalizedNoLoadDateTime <= onSiteDateTime.startOf('minute')
  ) {
    return true;
  }

  if (
    pickupDueDateTime?.isValid &&
    normalizedNoLoadDateTime <
      pickupDueDateTime.startOf('minute').plus({ minutes: noLoadMinWaitTime })
  ) {
    return true;
  }

  return false;
}

/**
 * Checks if a trip leg is ready to be invoiced.
 *
 * A trip leg is ready to invoice if it has:
 * - A driver (not required for LateCancel trips)
 * - A payable amount
 * - A status
 * - All required timestamps filled
 *
 * @param tripLeg - The trip leg to check
 * @returns true if ready to invoice, false otherwise
 */

export function checkTripLegReadyToInvoice(
  tripLeg: Pick<
    TripLegSearchResponseItem,
    'driverName' | 'tripLegStatusBC' | 'tripLegStatusLogs' | 'payableAmount'
  >,
): boolean {
  if (!tripLeg.tripLegStatusBC) {
    return false;
  }

  if (!tripLeg.payableAmount) {
    return false;
  }

  const isLateCancel = tripLeg.tripLegStatusBC === TripLegStatusBC.CanceledLate;

  if (!isLateCancel && !tripLeg.driverName) {
    return false;
  }

  return checkTimestampsFilled(tripLeg);
}
